'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Check, Zap, Gift } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { formatCurrency, getCurrencyMeta } from '@/lib/currency';
import { useTranslation } from '@/i18n';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricingPlan {
  id: string;
  name: string;
  plan_type: string;
  price_monthly: number;
  price_yearly: number;
  yearly_discount_percent: number;
  max_users: number;
  max_clients: number;
  features: string[];
  is_onboarding_plan: boolean;
  trial_duration_months: number;
  // derived / display
  tagline: string;
  highlight: boolean;
  badge?: string;
  cta: string;
}

// ─── Plan sort order ──────────────────────────────────────────────────────────

const PLAN_ORDER: Record<string, number> = {
  onboarding: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
  custom: 4,
};

function sortPlans(plans: PricingPlan[]): PricingPlan[] {
  return [...plans].sort((a, b) => {
    // Onboarding plan always first
    if (a.is_onboarding_plan && !b.is_onboarding_plan) return -1;
    if (!a.is_onboarding_plan && b.is_onboarding_plan) return 1;
    const ao = PLAN_ORDER[a.plan_type?.toLowerCase()] ?? 99;
    const bo = PLAN_ORDER[b.plan_type?.toLowerCase()] ?? 99;
    return ao - bo;
  });
}

function formatPercent(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  const rounded2 = Math.round(n * 100) / 100;
  if (Number.isInteger(rounded2)) return String(rounded2);
  return rounded2.toFixed(2).replace(/\.00$/, '');
}

function formatMoney(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function monthLabel(months: number, t: (k: string) => string, locale: string): string {
  const m = Number.isFinite(months) ? Math.max(0, Math.trunc(months)) : 0;
  return `${m} ${m === 1 ? (t('publicWebsite:pricing.time.month') || (locale === 'ar' ? 'شهر' : 'month')) : (t('publicWebsite:pricing.time.months') || (locale === 'ar' ? 'أشهر' : 'months'))}`;
}

function normalizeOnboardingFeatures(features: string[], months: number): string[] {
  const m = Number.isFinite(months) ? Math.max(0, Math.trunc(months)) : 0;
  return features
    .map((f) => {
      const s = String(f || '');
      const hasTrial = /\b\d+\s*-?\s*month(s)?\s*free\s*trial\b/i.test(s);
      if (!hasTrial) return s;
      if (m <= 0) return '';
      return `${m}-month free trial`;
    })
    .filter(Boolean);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LandingPricing() {
  const { t, locale } = useTranslation();
  const [annual, setAnnual] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<string>('AED');

  function translatePlanName(rawName: string): string {
    const n = String(rawName || '').trim();
    const nLow = n.toLowerCase();
    const NAME_MAP: Record<string, string> = {
      'free onboarding': t('publicWebsite:pricing.plans.onboarding.name') || (locale === 'ar' ? 'إعداد مجاني' : 'Free Onboarding'),
      'onboarding': t('publicWebsite:pricing.plans.onboarding.name') || (locale === 'ar' ? 'إعداد مجاني' : 'Free Onboarding'),
      'starter': t('publicWebsite:pricing.plans.starter.name') || (locale === 'ar' ? 'مبتدئ' : 'Starter'),
      'professional': t('publicWebsite:pricing.plans.professional.name') || (locale === 'ar' ? 'احترافي' : 'Professional'),
      'enterprise': t('publicWebsite:pricing.plans.enterprise.name') || (locale === 'ar' ? 'مؤسساتي' : 'Enterprise'),
      'custom': t('publicWebsite:pricing.plans.custom.name') || (locale === 'ar' ? 'مخصص' : 'Custom'),
    };
    return NAME_MAP[nLow] || n;
  }

  function translatePricingFeature(feature: string): string {
    const s = String(feature || '').trim();
    const sLow = s.toLowerCase();
    const FEATURE_MAP: Record<string, string> = {
      // Onboarding / Starter
      'up to 7 client companies': t('publicWebsite:pricing.features.upTo7ClientCompanies') || (locale === 'ar' ? 'حتى 7 شركات عملاء' : 'Up to 7 client companies'),
      'up to 3 team members': t('publicWebsite:pricing.features.upTo3TeamMembers') || (locale === 'ar' ? 'حتى 3 أعضاء في الفريق' : 'Up to 3 team members'),
      '2 team members': t('publicWebsite:pricing.features.2TeamMembers') || (locale === 'ar' ? 'عضوان في الفريق' : '2 team members'),
      'up to 10 team members': t('publicWebsite:pricing.features.upTo10TeamMembers') || (locale === 'ar' ? 'حتى 10 أعضاء في الفريق' : 'Up to 10 team members'),
      'document storage': t('publicWebsite:pricing.features.documentStorage') || (locale === 'ar' ? 'تخزين المستندات' : 'Document storage'),
      'document storage (5 gb)': t('publicWebsite:pricing.features.documentStorage5Gb') || (locale === 'ar' ? 'تخزين المستندات (5 جيجابايت)' : 'Document storage (5 GB)'),
      'document storage (50 gb)': t('publicWebsite:pricing.features.documentStorage50Gb') || (locale === 'ar' ? 'تخزين المستندات (50 جيجابايت)' : 'Document storage (50 GB)'),
      'trade licence & visa tracking': t('publicWebsite:pricing.features.tradeLicenceVisaTracking') || (locale === 'ar' ? 'تتبع الرخص التجارية والتأشيرات' : 'Trade licence & visa tracking'),
      'basic case management': t('publicWebsite:pricing.features.basicCaseManagement') || (locale === 'ar' ? 'إدارة أساسية للحالات' : 'Basic case management'),
      'invoice generation': t('publicWebsite:pricing.features.invoiceGeneration') || (locale === 'ar' ? 'إنشاء الفواتير' : 'Invoice generation'),
      'email renewal reminders': t('publicWebsite:pricing.features.emailRenewalReminders') || (locale === 'ar' ? 'تذكيرات التجديد عبر البريد الإلكتروني' : 'Email renewal reminders'),
      'standard support': t('publicWebsite:pricing.features.standardSupport') || (locale === 'ar' ? 'دعم قياسي' : 'Standard support'),
      '3-month free trial': t('publicWebsite:pricing.features.threeMonthFreeTrial') || (locale === 'ar' ? 'تجربة مجانية لمدة 3 أشهر' : '3-month free trial'),
      'no payment required': t('publicWebsite:pricing.features.noPaymentRequired') || (locale === 'ar' ? 'لا يلزم دفع' : 'No payment required'),
      // Professional
      'unlimited client companies': t('publicWebsite:pricing.features.unlimitedClientCompanies') || (locale === 'ar' ? 'شركات عملاء غير محدودة' : 'Unlimited client companies'),
      'up to 20 team members': t('publicWebsite:pricing.features.upTo20TeamMembers') || (locale === 'ar' ? 'حتى 20 عضواً في الفريق' : 'Up to 20 team members'),
      'advanced case workflows': t('publicWebsite:pricing.features.advancedCaseWorkflows') || (locale === 'ar' ? 'سير عمل متقدمة للحالات' : 'Advanced case workflows'),
      'client portal access': t('publicWebsite:pricing.features.clientPortalAccess') || (locale === 'ar' ? 'وصول بوابة العميل' : 'Client portal access'),
      'quotations & payment tracking': t('publicWebsite:pricing.features.quotationsPaymentTracking') || (locale === 'ar' ? 'عروض الأسعار وتتبع المدفوعات' : 'Quotations & payment tracking'),
      'role-based permissions': t('publicWebsite:pricing.features.roleBasedPermissions') || (locale === 'ar' ? 'أذونات تستند إلى الدور' : 'Role-based permissions'),
      'priority support': t('publicWebsite:pricing.features.prioritySupport') || (locale === 'ar' ? 'دعم ذو أولوية' : 'Priority support'),
      'activity audit logs': t('publicWebsite:pricing.features.activityAuditLogs') || (locale === 'ar' ? 'سجلات تدقيق النشاط' : 'Activity audit logs'),
      // Enterprise
      'all professional': t('publicWebsite:pricing.features.allProfessional') || (locale === 'ar' ? 'جميع ميزات الاحترافي' : 'All Professional features'),
      'all professional features': t('publicWebsite:pricing.features.allProfessional') || (locale === 'ar' ? 'جميع ميزات الاحترافي' : 'All Professional features'),
      'all starter': t('publicWebsite:pricing.features.allStarter') || (locale === 'ar' ? 'جميع ميزات المبتدئ' : 'All Starter features'),
      'all starter features': t('publicWebsite:pricing.features.allStarter') || (locale === 'ar' ? 'جميع ميزات المبتدئ' : 'All Starter features'),
      'custom integrations': t('publicWebsite:pricing.features.customIntegrations') || (locale === 'ar' ? 'تكاملات مخصصة' : 'Custom Integrations'),
      'dedicated support': t('publicWebsite:pricing.features.dedicatedSupport') || (locale === 'ar' ? 'دعم مخصص' : 'Dedicated Support'),
      'dedicated account manager': t('publicWebsite:pricing.features.dedicatedAccountManager') || (locale === 'ar' ? 'مدير حساب مخصص' : 'Dedicated account manager'),
      'sla': t('publicWebsite:pricing.features.sla') || (locale === 'ar' ? 'اتفاقية مستوى الخدمة' : 'SLA'),
      'sla-backed uptime': t('publicWebsite:pricing.features.slaBackedUptime') || (locale === 'ar' ? 'وقت تشغيل مدعوم باتفاقية مستوى الخدمة' : 'SLA-backed uptime'),
      'unlimited everything': t('publicWebsite:pricing.features.unlimitedEverything') || (locale === 'ar' ? 'كل شيء غير محدود' : 'Unlimited everything'),
      'unlimited team members': t('publicWebsite:pricing.features.unlimitedTeamMembers') || (locale === 'ar' ? 'أعضاء فريق غير محدودين' : 'Unlimited team members'),
      'unlimited document storage': t('publicWebsite:pricing.features.unlimitedStorage') || (locale === 'ar' ? 'تخزين مستندات غير محدود' : 'Unlimited document storage'),
      'on-premises option available': t('publicWebsite:pricing.features.onPremAvailable') || (locale === 'ar' ? 'خيار محلي متاح' : 'On-premises option available'),
      'custom role definitions': t('publicWebsite:pricing.features.customRoles') || (locale === 'ar' ? 'تعريفات أدوار مخصصة' : 'Custom role definitions'),
      'advanced reporting & analytics': t('publicWebsite:pricing.features.advancedReporting') || (locale === 'ar' ? 'تقارير وتحليلات متقدمة' : 'Advanced reporting & analytics'),
      'full access to all modules': t('publicWebsite:pricing.features.fullAllModules') || (locale === 'ar' ? 'وصول كامل إلى جميع الوحدات' : 'Full access to all modules'),
      // Smart AI (chips)
      'smart ai document upload': t('publicWebsite:pricing.smartAITools.label') || (locale === 'ar' ? 'رفع المستندات بالذكاء الاصطناعي الذكي' : 'Smart AI document upload'),
      'ai-powered document extraction included': t('publicWebsite:pricing.smartAITools.included') || (locale === 'ar' ? 'استخراج المستندات المدعوم بالذكاء الاصطناعي مضمّن' : 'AI-powered document extraction included'),
      'expiry and renewal intelligence': t('publicWebsite:pricing.smartAITools.expiry') || (locale === 'ar' ? 'ذكاء الانتهاء والتجديد' : 'Expiry and renewal intelligence'),
      'review before saving': t('publicWebsite:pricing.smartAITools.review') || (locale === 'ar' ? 'مراجعة قبل الحفظ' : 'Review before saving'),
    };
    const hit = FEATURE_MAP[sLow];
    if (hit) return hit;

    // catch-all: also map DB-returned "{count}-month free trial"
    const trialMatch = s.match(/^(\d+)-?month(?: free trial)?$/i);
    if (trialMatch) {
      const m = Number(trialMatch[1]);
      return (t('publicWebsite:pricing.features.nMonthFreeTrial') || (locale === 'ar' ? 'تجربة مجانية لمدة {months} شهر' : '{months}-month free trial')).replace('{months}', String(m));
    }
    const hasTrial2 = /^\d+\s*-?\s*month[s]?\s*free\s*trial$/i.test(s);
    if (hasTrial2) {
      const m = Number(String(s).match(/\d+/)?.[0] || '0');
      return (t('publicWebsite:pricing.features.nMonthFreeTrial') || (locale === 'ar' ? 'تجربة مجانية لمدة {months} شهر' : '{months}-month free trial')).replace('{months}', String(m));
    }
    return s;
  }

  function derivePlanMeta(plan: Omit<PricingPlan, 'tagline' | 'highlight' | 'badge' | 'cta'>): PricingPlan {
    const type = plan.plan_type?.toLowerCase() ?? '';
    const isEnterprise = type === 'enterprise' || type === 'custom' || (!plan.is_onboarding_plan && plan.price_monthly === 0);
    const isOnboarding = plan.is_onboarding_plan;

    const taglineMap: Record<string, string> = {
      starter: t('publicWebsite:pricing.plans.starter.tagline') || (locale === 'ar' ? 'للمكاتب الصغيرة لخدمات التمهيد التي بدأت للتو' : 'For small PRO offices just getting started'),
      professional: t('publicWebsite:pricing.plans.professional.tagline') || (locale === 'ar' ? 'لشركات خدمات التمهيد النامية التي تدير محافظات كبيرة' : 'For growing PRO companies managing large portfolios'),
      enterprise: t('publicWebsite:pricing.plans.enterprise.tagline') || (locale === 'ar' ? 'للشركات الكبيرة والعمليات متعددة الفروع' : 'For large PRO firms and multi-branch operations'),
      custom: t('publicWebsite:pricing.plans.custom.tagline') || (locale === 'ar' ? 'مصمم لعمليات الحجم المؤسسي' : 'Tailored for enterprise-scale operations'),
    };

    const trialMonths = plan.trial_duration_months ?? 3;
    const safeTrialMonths = Number.isFinite(trialMonths) ? Math.max(0, Math.trunc(trialMonths)) : 0;

    let badge: string | undefined;
    if (isOnboarding) {
      badge = safeTrialMonths > 0
        ? (safeTrialMonths === 1
            ? (t('publicWebsite:pricing.plans.onboarding.badgeMonth') || (locale === 'ar' ? '{months} شهر مجاني' : '{months} Month Free')).replace('{months}', String(safeTrialMonths))
            : (t('publicWebsite:pricing.plans.onboarding.badgeMonths') || (locale === 'ar' ? '{months} أشهر مجانية' : '{months} Months Free')).replace('{months}', String(safeTrialMonths)))
        : t('publicWebsite:pricing.plans.onboarding.badgeFree') || (locale === 'ar' ? 'إعداد مجاني' : 'Free Onboarding');
    } else if (type === 'professional') {
      badge = t('publicWebsite:pricing.plans.professional.badge') || (locale === 'ar' ? 'الأكثر شيوعاً' : 'Most Popular');
    }

    const cta = isOnboarding
      ? t('publicWebsite:pricing.plans.onboarding.cta') || (locale === 'ar' ? 'ابدأ التجربة المجانية' : 'Start Free Trial')
      : isEnterprise
        ? t('publicWebsite:pricing.plans.enterprise.cta') || (locale === 'ar' ? 'تواصل مع المبيعات' : 'Contact Sales')
        : t('publicWebsite:pricing.plans.professional.cta') || (locale === 'ar' ? 'ابدأ الآن' : 'Get Started');

    return {
      ...plan,
      name: translatePlanName(plan.name),
      tagline: isOnboarding
        ? t('publicWebsite:pricing.plans.onboarding.tagline') || (locale === 'ar' ? 'ابدأ مجاناً بدون دفع مطلوب' : 'Start free with no payment required')
        : (taglineMap[type] ?? `${plan.name} plan`),
      highlight: type === 'professional',
      badge,
      cta,
    };
  }

  const FALLBACK_BASE_PLANS: Array<Omit<PricingPlan, 'tagline' | 'highlight' | 'badge' | 'cta'>> = [
    {
      id: 'fallback-onboarding',
      name: t('publicWebsite:pricing.plans.onboarding.name') || (locale === 'ar' ? 'إعداد مجاني' : 'Free Onboarding'),
      plan_type: 'onboarding',
      price_monthly: 0,
      price_yearly: 0,
      yearly_discount_percent: 0,
      max_users: 3,
      max_clients: 7,
      trial_duration_months: 3,
      is_onboarding_plan: true,
      features: [
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.clients7') || (locale === 'ar' ? 'حتى 7 شركات عملاء' : 'Up to 7 client companies'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.team3') || (locale === 'ar' ? 'حتى 3 أعضاء في الفريق' : 'Up to 3 team members'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.docStorage') || (locale === 'ar' ? 'تخزين المستندات' : 'Document storage'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.licenceVisa') || (locale === 'ar' ? 'تتبع الرخص التجارية والتأشيرات' : 'Trade licence & visa tracking'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.cases') || (locale === 'ar' ? 'إدارة أساسية للحالات' : 'Basic case management'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.invoices') || (locale === 'ar' ? 'إنشاء الفواتير' : 'Invoice generation'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.reminders') || (locale === 'ar' ? 'تذكيرات التجديد عبر البريد الإلكتروني' : 'Email renewal reminders'),
        t('publicWebsite:pricing.plans.fallbackFeatures.onboarding.support') || (locale === 'ar' ? 'دعم قياسي' : 'Standard support'),
      ],
    },
    {
      id: 'fallback-professional',
      name: t('publicWebsite:pricing.plans.professional.name') || (locale === 'ar' ? 'احترافي' : 'Professional'),
      plan_type: 'professional',
      price_monthly: 699,
      price_yearly: 6990,
      yearly_discount_percent: 16.67,
      max_users: 20,
      max_clients: 999,
      trial_duration_months: 0,
      is_onboarding_plan: false,
      features: [
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.clientsUnlimited') || (locale === 'ar' ? 'شركات عملاء غير محدودة' : 'Unlimited client companies'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.team20') || (locale === 'ar' ? 'حتى 20 عضواً في الفريق' : 'Up to 20 team members'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.doc50gb') || (locale === 'ar' ? 'تخزين المستندات (50 جيجابايت)' : 'Document storage (50 GB)'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.advancedCases') || (locale === 'ar' ? 'سير عمل متقدمة للحالات' : 'Advanced case workflows'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.clientPortal') || (locale === 'ar' ? 'وصول بوابة العميل' : 'Client portal access'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.quotations') || (locale === 'ar' ? 'عروض الأسعار وتتبع المدفوعات' : 'Quotations & payment tracking'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.permissions') || (locale === 'ar' ? 'أذونات تستند إلى الدور' : 'Role-based permissions'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.prioritySupport') || (locale === 'ar' ? 'دعم ذو أولوية' : 'Priority support'),
        t('publicWebsite:pricing.plans.fallbackFeatures.professional.auditLogs') || (locale === 'ar' ? 'سجلات تدقيق النشاط' : 'Activity audit logs'),
      ],
    },
    {
      id: 'fallback-enterprise',
      name: t('publicWebsite:pricing.plans.enterprise.name') || (locale === 'ar' ? 'مؤسساتي' : 'Enterprise'),
      plan_type: 'enterprise',
      price_monthly: 0,
      price_yearly: 0,
      yearly_discount_percent: 0,
      max_users: 0,
      max_clients: 0,
      trial_duration_months: 0,
      is_onboarding_plan: false,
      features: [
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.unlimitedEverything') || (locale === 'ar' ? 'كل شيء غير محدود' : 'Unlimited everything'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.unlimitedTeam') || (locale === 'ar' ? 'أعضاء فريق غير محدودين' : 'Unlimited team members'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.unlimitedStorage') || (locale === 'ar' ? 'تخزين مستندات غير محدود' : 'Unlimited document storage'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.allProfessional') || (locale === 'ar' ? 'جميع ميزات الاحترافي' : 'All Professional features'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.integrations') || (locale === 'ar' ? 'تكاملات مخصصة' : 'Custom integrations'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.accountManager') || (locale === 'ar' ? 'مدير حساب مخصص' : 'Dedicated account manager'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.sla') || (locale === 'ar' ? 'وقت تشغيل مدعوم باتفاقية مستوى الخدمة' : 'SLA-backed uptime'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.onPrem') || (locale === 'ar' ? 'خيار محلي متاح' : 'On-premises option available'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.customRoles') || (locale === 'ar' ? 'تعريفات أدوار مخصصة' : 'Custom role definitions'),
        t('publicWebsite:pricing.plans.fallbackFeatures.enterprise.advancedReporting') || (locale === 'ar' ? 'تقارير وتحليلات متقدمة' : 'Advanced reporting & analytics'),
      ],
    },
  ];

  const FALLBACK_PLANS: PricingPlan[] = sortPlans(FALLBACK_BASE_PLANS.map(derivePlanMeta));
  const [plans, setPlans] = useState<PricingPlan[]>(FALLBACK_PLANS);

  const bestAnnualDiscount = plans.reduce((acc, p) => {
    if (p.is_onboarding_plan) return acc;
    const type = p.plan_type?.toLowerCase() ?? '';
    const isCustom = p.price_monthly === 0 || type === 'enterprise' || type === 'custom';
    if (isCustom) return acc;
    return Math.max(acc, Number(p.yearly_discount_percent || 0));
  }, 0);

  useEffect(() => {
    async function fetchPlans() {
      try {
        const supabase = createClient();

        // Single query — fetch ALL active plans (onboarding + paid)
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('id, name, plan_type, price_monthly, price_yearly, yearly_discount_percent, max_users, max_clients, features, is_onboarding_plan, trial_duration_months')
          .eq('is_active', true);

        if (error) {
          setFetchError((t('publicWebsite:pricing.errors.supabase') || (locale === 'ar' ? 'خطأ في Supabase: {message}' : 'Supabase error: {message}')).replace('{message}', error.message));
          setPlans(FALLBACK_PLANS);
          return;
        }

        // If Supabase returns zero plans → use fallback only
        if (!data || data.length === 0) {
          setFetchError(t('publicWebsite:pricing.errors.noActivePlans') || (locale === 'ar' ? 'لا توجد خطط نشطة مرتاحة من Supabase.' : 'No active plans returned from Supabase.'));
          setPlans(FALLBACK_PLANS);
          return;
        }

        // Supabase returned at least one plan — use ONLY Supabase data, never merge with fallback
        let mapped: PricingPlan[] = data.map((row: any) =>
          derivePlanMeta({
            id: row.id,
            name: row.name,
            plan_type: row.plan_type ?? '',
            price_monthly: row.price_monthly ?? 0,
            price_yearly: row.price_yearly ?? 0,
            yearly_discount_percent: row.yearly_discount_percent ?? 0,
            max_users: row.max_users ?? 0,
            max_clients: row.max_clients ?? 0,
            features: Array.isArray(row.features) ? row.features : [],
            is_onboarding_plan: row.is_onboarding_plan ?? false,
            trial_duration_months: row.trial_duration_months ?? 3,
          })
        );

        if (!mapped.some((p) => p.is_onboarding_plan)) {
          const fallbackIndex = mapped.findIndex((p) => {
            const name = (p.name || '').toLowerCase();
            const type = (p.plan_type || '').toLowerCase();
            return (name.includes('free onboarding') || name.includes('onboarding')) && (p.price_monthly === 0 || type === 'starter');
          });
          if (fallbackIndex >= 0) {
            mapped = mapped.map((p, idx) => idx === fallbackIndex ? { ...p, is_onboarding_plan: true } : p);
          }
        }

        const sorted = sortPlans(mapped);
        setFetchError(null);
        setPlans(sorted);
      } catch (err: unknown) {
        // Network or unexpected error → use fallback only
        const msg = err instanceof Error ? err.message : String(err);
        setFetchError((t('publicWebsite:pricing.errors.unexpected') || (locale === 'ar' ? 'خطأ غير متوقع: {message}' : 'Unexpected error: {message}')).replace('{message}', msg));
        setPlans(FALLBACK_PLANS);
      }
    }
    fetchPlans();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function fetchCurrency() {
      try {
        const supabase = createClient();
        const { data } = await supabase.from('platform_public_settings').select('default_currency').limit(1).maybeSingle();
        const code = String((data as any)?.default_currency || 'AED').trim().toUpperCase();
        setDefaultCurrency(code || 'AED');
      } catch {
        setDefaultCurrency('AED');
      }
    }
    fetchCurrency();
  }, []);

  const currencyCode = defaultCurrency || 'AED';
  const currencyLabel = getCurrencyMeta(currencyCode).code;

  const includedChips = [
    t('publicWebsite:pricing.includedFeatures.smartUpload') || (locale === 'ar' ? 'رفع المستندات بالذكاء الاصطناعي الذكي' : 'Smart AI document upload'),
    t('publicWebsite:pricing.includedFeatures.aiExtraction') || (locale === 'ar' ? 'استخراج المستندات بالذكاء الاصطناعي' : 'AI-powered document extraction'),
    t('publicWebsite:pricing.includedFeatures.expiryIntel') || (locale === 'ar' ? 'ذكاء الانتهاء والتجديد' : 'Expiry and renewal intelligence'),
    t('publicWebsite:pricing.includedFeatures.biDashboard') || (locale === 'ar' ? 'لوحة ذكاء الأعمال' : 'Business intelligence dashboard'),
    t('publicWebsite:pricing.includedFeatures.reviewBeforeSave') || (locale === 'ar' ? 'مراجعة قبل الحفظ' : 'Review before saving'),
  ];

  return (
    <section id="pricing" className="py-24 bg-background">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Section header ──────────────────────────────────────────────── */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="section-label text-accent mb-3">{t('publicWebsite:pricing.sectionLabel') || (locale === 'ar' ? 'الأسعار' : 'Pricing')}</p>
          <h2 className="text-4xl font-bold text-foreground mb-4">
            {(t('publicWebsite:pricing.title') || (locale === 'ar' ? 'أسعار شفافة بعملة {currency}' : 'Transparent Pricing in {currency}')).replace('{currency}', currencyLabel)}
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            {(() => {
              const onboarding = plans.find((p) => p.is_onboarding_plan);
              const months = onboarding?.trial_duration_months ?? 0;
              const safeMonths = Number.isFinite(months) ? Math.max(0, Math.trunc(months)) : 0;
              if (!onboarding || safeMonths <= 0) {
                return t('publicWebsite:pricing.subtitleFree') || (locale === 'ar' ? 'ابدأ مجاناً مع الإعداد. لا رسوم إعداد. لا رسوم خفية.' : 'Start free with onboarding. No setup fees. No hidden charges.');
              }
              return (t('publicWebsite:pricing.subtitleTrial') || (locale === 'ar' ? 'ابدأ مجاناً لمدة {period}. لا رسوم إعداد. لا رسوم خفية.' : 'Start free for {period}. No setup fees. No hidden charges.'))
                .replace('{period}', monthLabel(safeMonths, t, locale));
            })()}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8 text-xs sm:text-sm">
            {includedChips.map((item) => (
              <span key={item} className="rounded-full border border-primary/15 bg-primary/[0.05] px-3 py-1.5 font-medium text-foreground">
                {item}
              </span>
            ))}
          </div>

          {/* Toggle */}
          <div className="inline-flex items-center gap-3 bg-muted rounded-xl p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                !annual ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {t('publicWebsite:pricing.toggle.monthly') || (locale === 'ar' ? 'شهري' : 'Monthly')}
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center gap-1.5 ${
                annual ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {t('publicWebsite:pricing.toggle.annual') || (locale === 'ar' ? 'سنوي' : 'Annual')}
              {bestAnnualDiscount > 0 && (
                <span className="text-xs font-bold text-success bg-success/10 px-1.5 py-0.5 rounded-full">
                  {(t('publicWebsite:pricing.toggle.savePercent') || (locale === 'ar' ? 'وفّر {percent}٪' : 'Save {percent}%')).replace('{percent}', formatPercent(bestAnnualDiscount))}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ── Fallback notice (dev/debug only — non-blocking) ─────────────── */}
        {fetchError && (
          <div className="mb-6 max-w-xl mx-auto bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-xs text-yellow-800">
            <strong>{(t('publicWebsite:pricing.note') || (locale === 'ar' ? 'ملاحظة: عرض أسعار بديلة. السبب: {reason}' : 'Note: Showing fallback pricing. Reason: {reason}')).split(':')[0]}:</strong>
            {(t('publicWebsite:pricing.note') || (locale === 'ar' ? 'ملاحظة: عرض أسعار بديلة. السبب: {reason}' : 'Note: Showing fallback pricing. Reason: {reason}')).replace('{reason}', fetchError).split(':').slice(1).join(':').trim()}
          </div>
        )}

        {/* ── Plan cards ──────────────────────────────────────────────────── */}
        <div className={`grid grid-cols-1 gap-6 items-start ${plans.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {plans.map((plan) => {
            const isOnboarding = plan.is_onboarding_plan;
            const isCustom = !isOnboarding && (plan.price_monthly === 0 || plan.plan_type?.toLowerCase() === 'enterprise' || plan.plan_type?.toLowerCase() === 'custom');
            const displayPrice = annual ? plan.price_yearly : plan.price_monthly;
            const trialMonths = Number.isFinite(plan.trial_duration_months) ? Math.max(0, Math.trunc(plan.trial_duration_months)) : 0;
            const maxClients = plan.max_clients ?? 7;
            const discount = Number(plan.yearly_discount_percent || 0);
            const showAnnualHelper = annual && !isOnboarding && !isCustom && discount > 0 && plan.price_yearly > 0;
            const equivalentMonthly = showAnnualHelper ? (plan.price_yearly / 12) : 0;
            const displayFeatures = isOnboarding ? normalizeOnboardingFeatures(plan.features, trialMonths) : plan.features;

            const smartAiTools = (
              <div className="rounded-xl border border-primary/15 bg-primary/[0.05] px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                  {t('publicWebsite:pricing.smartAITools.label') || (locale === 'ar' ? 'أدوات المستندات بالذكاء الاصطناعي الذكي' : 'Smart AI document tools')}
                </p>
                <ul className="space-y-1.5 text-sm text-foreground">
                  <li>{t('publicWebsite:pricing.smartAITools.included') || (locale === 'ar' ? 'استخراج المستندات المدعوم بالذكاء الاصطناعي مضمّن' : 'AI-powered document extraction included')}</li>
                  <li>{t('publicWebsite:pricing.smartAITools.expiry') || (locale === 'ar' ? 'ذكاء الانتهاء والتجديد' : 'Expiry and renewal intelligence')}</li>
                  <li>{t('publicWebsite:pricing.smartAITools.review') || (locale === 'ar' ? 'مراجعة قبل الحفظ' : 'Review before saving')}</li>
                </ul>
              </div>
            );

            return (
              <div
                key={plan.id}
                className={`card-base p-7 flex flex-col gap-6 relative ${
                  isOnboarding
                    ? 'border-success/40 ring-2 ring-success/20'
                    : plan.highlight
                    ? 'border-accent ring-2 ring-accent/30 shadow-card-hover'
                    : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-white text-xs font-bold shadow-md ${isOnboarding ? 'bg-success' : 'gradient-accent'}`}>
                      {isOnboarding ? <Gift className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-bold text-foreground mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.tagline}</p>
                </div>

                {smartAiTools}

                <div className="flex items-end gap-1">
                  {isOnboarding ? (
                    <div>
                      <div className="flex items-end gap-1">
                        <span className="text-4xl font-bold text-foreground tabular-nums">
                          {formatCurrency(0, currencyCode)}
                        </span>
                        {trialMonths > 0 && (
                          <span className="text-muted-foreground text-sm mb-1">
                            {(t('publicWebsite:pricing.plans.onboarding.period') || (locale === 'ar' ? '/ {period}' : '/ {period}')).replace('{period}', monthLabel(trialMonths, t, locale))}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-success font-medium mt-1">
                        {t('publicWebsite:pricing.plans.onboarding.noCard') || (locale === 'ar' ? 'لا يلزم بطاقة ائتمان' : 'No credit card required')}
                      </p>
                    </div>
                  ) : isCustom ? (
                    <div>
                      <span className="text-3xl font-bold text-foreground">
                        {t('publicWebsite:pricing.plans.enterprise.customPricing') || (locale === 'ar' ? 'تسعير مخصص' : 'Custom Pricing')}
                      </span>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('publicWebsite:pricing.plans.enterprise.customQuote') || (locale === 'ar' ? 'تواصل معنا للحصول على عرض مخصص' : 'Contact us for a tailored quote')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-foreground tabular-nums">
                        {formatCurrency(displayPrice, currencyCode, { maxDecimals: 0 })}
                      </span>
                      <span className="text-muted-foreground text-sm mb-1">
                        {annual ? (t('publicWebsite:pricing.plans.yearSuffix') || (locale === 'ar' ? '/سنة' : '/year')) : (t('publicWebsite:pricing.plans.monthSuffix') || (locale === 'ar' ? '/شهر' : '/month'))}
                      </span>
                    </>
                  )}
                </div>

                {showAnnualHelper && (
                  <p className="text-xs text-muted-foreground -mt-3">
                    {(t('publicWebsite:pricing.plans.equivalentMonthly') || (locale === 'ar' ? 'يعادل {amount}/شهر' : 'Equivalent to {amount}/month'))
                      .replace('{amount}', formatCurrency(equivalentMonthly, currencyCode, { maxDecimals: 0 }))}
                  </p>
                )}

                {isOnboarding && (
                  <div className="bg-success/5 border border-success/20 rounded-lg px-3 py-2 text-xs text-success font-medium">
                    {(t('publicWebsite:pricing.plans.onboarding.clientLimit') || (locale === 'ar' ? 'حتى {count} شركة عملاء{trial}' : 'Up to {count} client companies{trial}'))
                      .replace('{count}', String(maxClients))
                      .replace('{trial}', trialMonths > 0 ? ` · ${(t('publicWebsite:pricing.features.nMonthFreeTrial') || (locale === 'ar' ? 'تجربة مجانية لمدة {months} شهر' : '{months}-month free trial')).replace('{months}', String(trialMonths))}` : '')}
                  </div>
                )}

                <ul className="space-y-2.5 flex-1">
                  {displayFeatures.map((f, fi) => (
                    <li
                      key={`${plan.id}-feat-${fi}`}
                      className="flex items-start gap-2.5 text-sm text-foreground"
                    >
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-success" />
                      {translatePricingFeature(f)}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/sign-up-login-screen"
                  className={
                    isOnboarding
                      ? 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-success text-white text-sm font-semibold hover:bg-success/90 transition-colors w-full'
                      : plan.highlight
                      ? 'btn-accent w-full justify-center'
                      : 'btn-outline w-full justify-center'
                  }
                >
                  {isOnboarding && <Zap className="w-4 h-4" />}
                  {plan.cta}
                </Link>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          {(t('publicWebsite:pricing.footer') || (locale === 'ar' ? 'جميع الأسعار معروضة بعملة {currency} ولا تشمل الضرائب. قد تنطبق الضرائب حسب اختصاصك القضائي.' : 'All prices are shown in {currency} and exclusive of taxes. Taxes may apply depending on your jurisdiction.'))
            .replace('{currency}', currencyLabel)}
        </p>
      </div>
    </section>
  );
}
